import React, { useState, useEffect, useRef } from 'react';
import { usePermissions } from '../../context/PermissionsContext.tsx';
import {
  X,
  CheckCircle2,
  Clock,
  Tag,
  Paperclip,
  MessageSquare,
  Plus,
  Trash2,
  Archive,
  RotateCcw,
  History,
  Copy,
  CheckCheck,
  Send,
  FileText,
  Download,
  Timer,
  ShieldAlert,
  UploadCloud,
  Eye,
  Palette,
  ArrowRightLeft,
  Share2,
  Edit3,
  CornerUpRight,
  Image as ImageIcon,
  Users,
  Layers,
  Package,
  Lock,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import {
  Task,
  TaskStatus,
  PriorityLevel,
  User,
  TaskTimeEntry,
  CardLabel,
  Project,
  ProjectBuild,
  ProjectFeature,
  canTransitionTask,
  checkTaskTransition,
} from '../../../shared/types.ts';
import { RoleBadge } from '../common/Badge.tsx';
import { Avatar } from '../common/Avatar.tsx';
import { LinkifiedText } from '../common/LinkifiedText.tsx';
import { DescriptionEditor } from '../common/DescriptionEditor.tsx';
import { TRELLO_LABEL_PALETTE, TRELLO_COVER_COLORS } from '../projects/KanbanBoard.tsx';
import { DocumentPreviewModal } from '../common/DocumentPreviewModal.tsx';
import { ExistingProjectMembersModal } from '../projects/ExistingProjectMembersModal.tsx';
import { RequestChangesModal } from './RequestChangesModal.tsx';
import { describeUploadFailure } from '../../services/uploadErrors.ts';
import { useEscapeKey } from '../../hooks/useEscapeKey.ts';

interface TaskDetailModalProps {
  taskId: string;
  isOpen: boolean;
  onClose: () => void;
  onTaskUpdated: () => void;
  projectMembers?: User[];
}

export const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  taskId,
  isOpen,
  onClose,
  onTaskUpdated,
  projectMembers,
}) => {
  useEscapeKey(isOpen, onClose);
  const { token, user } = useAuth();
  const [task, setTask] = useState<any | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [projectMembersMap, setProjectMembersMap] = useState<Record<string, User[]>>({});

  // Editable fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<TaskStatus>('todo');
  const [priority, setPriority] = useState<PriorityLevel>('medium');
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [buildId, setBuildId] = useState<string | null>(null);
  const [featureId, setFeatureId] = useState<string | null>(null);
  const [projectBuilds, setProjectBuilds] = useState<ProjectBuild[]>([]);
  const [projectFeatures, setProjectFeatures] = useState<ProjectFeature[]>([]);
  const [dueDate, setDueDate] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [isViewingProjectMembers, setIsViewingProjectMembers] = useState(false);

  // Scope assignable card members to the board/project's attached members when available.
  // If no project is selected or project has no specific members, show all team members.
  const scopedMembers = React.useMemo(() => {
    const pid = task?.projectId;
    if (pid) {
      if (projectMembersMap[pid] && projectMembersMap[pid].length > 0) {
        return projectMembersMap[pid];
      }
      const proj = allProjects.find((p) => p.id === pid);
      if (proj && Array.isArray(proj.members) && proj.members.length > 0) {
        return proj.members;
      }
      if (Array.isArray(projectMembers) && projectMembers.length > 0) {
        return projectMembers;
      }
    }
    return teamMembers;
  }, [task?.projectId, projectMembersMap, allProjects, projectMembers, teamMembers]);

  // Ensure current assignee is included in the list even if external
  const displayedScopedMembers = React.useMemo(() => {
    let list = [...scopedMembers];
    if (assigneeId && !list.some((m) => m.id === assigneeId)) {
      const found = teamMembers.find((m) => m.id === assigneeId);
      if (found) {
        list.push(found);
      } else if (task?.assigneeName) {
        list.push({
          id: assigneeId,
          fullName: task.assigneeName,
          avatarUrl: task.assigneeAvatar,
          role: 'Member',
          email: '',
        } as any);
      }
    }
    return list;
  }, [scopedMembers, assigneeId, teamMembers, task?.assigneeName, task?.assigneeAvatar]);

  // Active view tab: Details vs Activity & Accountability Log
  const [activeTab, setActiveTab] = useState<'details' | 'history'>('details');
  const [historyLogs, setHistoryLogs] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isCopiedId, setIsCopiedId] = useState(false);
  const [isCopiedLink, setIsCopiedLink] = useState(false);

  // Trello Popovers in Modal
  const [activePopover, setActivePopover] = useState<
    null | 'labels' | 'cover' | 'members' | 'move'
  >(null);
  const [customLabelNames, setCustomLabelNames] = useState<Record<string, string>>({});
  const [moveTargetProjectId, setMoveTargetProjectId] = useState<string>('');
  const [moveTargetStatus, setMoveTargetStatus] = useState<TaskStatus>('todo');

  // Checklists & Comments
  const [newChecklistTitle, setNewChecklistTitle] = useState('');
  const [hideCompletedChecklists, setHideCompletedChecklists] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Time Tracking entries state
  const [timeEntries, setTimeEntries] = useState<TaskTimeEntry[]>([]);
  const [isLoggingTime, setIsLoggingTime] = useState(false);
  const [logHours, setLogHours] = useState('1.5');
  const [logDesc, setLogDesc] = useState('');
  const [logBillable, setLogBillable] = useState(true);
  const [logRate, setLogRate] = useState('85');

  // RBAC & Review Approval helpers
  const isWorkspaceAdmin = user?.role === 'admin';
  const isProjectLead = isWorkspaceAdmin || user?.role === 'team_lead' || user?.role === 'manager';
  const { can } = usePermissions();
  const isViewer = user?.role === 'viewer' && !can('task.edit');
  const isGuest = user?.role === 'guest' && !can('task.edit');

  const currentUserForPermissions = React.useMemo(() => ({
    id: user?.id || '',
    workspaceRole: isWorkspaceAdmin ? 'ADMIN' : undefined,
    // what a workspace role may review is decided by the role permissions, through `role`
    projectRole: isViewer ? 'VIEWER' : isGuest ? 'GUEST' : 'MEMBER',
    role: user?.role,
  }), [user?.id, user?.role, isWorkspaceAdmin, isViewer, isGuest]);

  const canApproveTask = (t?: any): boolean => {
    if (!t) return false;
    return canTransitionTask(currentUserForPermissions, t, 'COMPLETED');
  };

  const [isRequestChangesOpen, setIsRequestChangesOpen] = useState(false);
  const [isSubmittingReviewAction, setIsSubmittingReviewAction] = useState(false);

  const getAuthHeaders = (extra: Record<string, string> = {}) => ({
    Authorization: `Bearer ${token || ''}`,
    ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
    ...extra,
  });

  // Re-fetching the task that is already open (after a status change etc.) updates it quietly
  // instead of replacing the whole window with a spinner.
  const loadedTaskIdRef = useRef<string | null>(null);

  const fetchTask = async () => {
    if (!token || !taskId) return;
    const isBackgroundRefresh = loadedTaskIdRef.current === taskId;
    if (!isBackgroundRefresh) {
      setIsLoading(true);
      setLoadError(null);
    }
    try {
      const res = await fetch(`/api/tasks/${taskId}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const t = data.task;
        loadedTaskIdRef.current = taskId;
        setTask(t);
        setTitle(t.title);
        setDescription(t.description || '');
        setStatus(t.status);
        setPriority(t.priority);
        setAssigneeId(t.assigneeId || null);
        setBuildId(t.buildId || null);
        setFeatureId(t.featureId || null);
        setDueDate(t.dueDate ? String(t.dueDate).slice(0, 10) : '');
        setStartDate(t.startDate ? String(t.startDate).slice(0, 10) : '');
        setMoveTargetProjectId(t.projectId || '');
        setMoveTargetStatus(t.status || 'todo');
        setLoadError(null);

        if (t.projectId) {
          fetch(`/api/projects/${t.projectId}/builds`, { headers: getAuthHeaders() })
            .then((r) => r.json())
            .then((d) => setProjectBuilds(d.builds || []))
            .catch(() => {});
          fetch(`/api/projects/${t.projectId}/features`, { headers: getAuthHeaders() })
            .then((r) => r.json())
            .then((d) => setProjectFeatures(d.features || []))
            .catch(() => {});
        }
      } else if (!isBackgroundRefresh) {
        const errData = await res.json().catch(() => ({}));
        setTask(null);
        setLoadError(errData.error || 'Card / Task not found or has been permanently deleted.');
      }
    } catch (err: any) {
      console.error('Failed to load task:', err);
      if (!isBackgroundRefresh) setLoadError(err.message || 'Unable to load task details.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTimeEntries = async () => {
    if (!token || !taskId) return;
    try {
      const res = await fetch(`/api/features/time-entries/${taskId}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setTimeEntries(data.entries || []);
      }
    } catch (err) {
      console.error('Failed to load time entries:', err);
    }
  };

  const fetchHistory = async () => {
    if (!token || !taskId) return;
    if (historyLogs.length === 0) setIsLoadingHistory(true);
    try {
      const res = await fetch(`/api/tasks/${taskId}/history`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setHistoryLogs(data.history || []);
      }
    } catch (err) {
      console.error('Failed to load task history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadedTaskIdRef.current = null;
      setTask(null);
      setIsLoading(true);
      setLoadError(null);
      fetchTask();
      fetchTimeEntries();
      fetchHistory();
      fetch('/api/team/members', { headers: getAuthHeaders() })
        .then((r) => r.json())
        .then((d) => setTeamMembers(d.members || []))
        .catch(() => {});
      fetch('/api/projects?allSpaces=true', { headers: getAuthHeaders() })
        .then((r) => r.json())
        .then((d) => setAllProjects(d.projects || []))
        .catch(() => {});
    }
  }, [isOpen, taskId, token, user?.id]);

  // Dynamically load members of current card's project
  useEffect(() => {
    const pid = task?.projectId;
    if (pid && !projectMembersMap[pid] && token) {
      fetch(`/api/projects/${pid}`, { headers: getAuthHeaders() })
        .then((r) => r.json())
        .then((d) => {
          if (d.project && Array.isArray(d.project.members) && d.project.members.length > 0) {
            setProjectMembersMap((prev) => ({ ...prev, [pid]: d.project.members }));
          }
        })
        .catch(() => {});
    }
  }, [task?.projectId, token, projectMembersMap]);

  const handleArchiveTask = async () => {
    if (
      !confirm(
        'Move this card to the Bin / Archive? It will not be permanently deleted and can be restored anytime.'
      )
    )
      return;
    try {
      const res = await fetch(`/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        window.dispatchEvent(
          new CustomEvent('task-archived', { detail: { taskId, projectId: task?.projectId } })
        );
        await fetchTask();
        await fetchHistory();
        onTaskUpdated();
      }
    } catch (err) {
      console.error('Failed to move task to bin:', err);
    }
  };

  const handlePermanentDeleteTask = async () => {
    if (
      !confirm(
        'Permanently delete this card from the Bin? This action cannot be undone.'
      )
    )
      return;
    try {
      const res = await fetch(`/api/tasks/${taskId}/permanent`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        onTaskUpdated();
        onClose();
      }
    } catch (err) {
      console.error('Failed to permanently delete task:', err);
    }
  };

  const handleRestoreTask = async () => {
    try {
      const res = await fetch(`/api/tasks/${taskId}/restore`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        window.dispatchEvent(
          new CustomEvent('task-restored', {
            detail: data.task || { taskId, projectId: task?.projectId },
          })
        );
        await fetchTask();
        await fetchHistory();
        onTaskUpdated();
      }
    } catch (err) {
      console.error('Failed to restore task:', err);
    }
  };

  const handleToggleWatch = async () => {
    if (!token || !task) return;
    try {
      const res = await fetch(`/api/tasks/${taskId}/watch`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        await fetchTask();
        onTaskUpdated();
      }
    } catch (err) {
      console.error('Failed to toggle watch:', err);
    }
  };

  const handleDuplicateCard = async () => {
    if (!token || !task) return;
    try {
      const res = await fetch(`/api/tasks/${taskId}/duplicate`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.task) {
          window.dispatchEvent(new CustomEvent('task-created', { detail: data.task }));
        }
        onTaskUpdated();
        onClose();
      }
    } catch (err) {
      console.error('Failed to duplicate card:', err);
    }
  };

  const handleMoveCard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !task) return;
    try {
      const res = await fetch(`/api/tasks/${taskId}/move`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          projectId: moveTargetProjectId || task.projectId,
          status: moveTargetStatus,
          listId: null,
        }),
      });
      if (res.ok) {
        setActivePopover(null);
        await fetchTask();
        await fetchHistory();
        onTaskUpdated();
      }
    } catch (err) {
      console.error('Failed to move card:', err);
    }
  };

  const handleToggleColoredLabel = async (paletteItem: (typeof TRELLO_LABEL_PALETTE)[0]) => {
    if (!task) return;
    const current: CardLabel[] = task.coloredLabels || [];
    const exists = current.some((l) => l.color === paletteItem.color);
    const customName =
      customLabelNames[paletteItem.color] !== undefined
        ? customLabelNames[paletteItem.color]
        : paletteItem.name;
    const next: CardLabel[] = exists
      ? current.filter((l) => l.color !== paletteItem.color)
      : [
          ...current,
          { id: paletteItem.color, name: customName, color: paletteItem.color },
        ];
    await handleSaveField({ coloredLabels: next } as any);
  };

  const handleToggleMemberAssignment = async (memberId: string) => {
    if (!task) return;
    const currentIds: string[] =
      Array.isArray(task.assignees) && task.assignees.length > 0
        ? task.assignees.map((a: any) => a.id)
        : task.assigneeId
        ? [task.assigneeId]
        : [];
    const exists = currentIds.includes(memberId);
    const nextIds = exists
      ? currentIds.filter((id) => id !== memberId)
      : [memberId, ...currentIds.filter((id) => id !== memberId)];
    const nextAssigneeId = nextIds[0] || null;
    setAssigneeId(nextAssigneeId);
    await handleSaveField({
      assigneeIds: nextIds,
      assigneeId: nextAssigneeId,
    } as any);
  };

  const handleLogTime = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    try {
      const res = await fetch('/api/features/time-entries', {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          taskId,
          hours: parseFloat(logHours) || 1.0,
          description: logDesc.trim() || 'Work logged on task',
          isBillable: logBillable,
          hourlyRate: parseFloat(logRate) || 85.0,
        }),
      });

      if (res.ok) {
        fetchTimeEntries();
        fetchTask();
        onTaskUpdated();
        setIsLoggingTime(false);
        setLogDesc('');
      }
    } catch (err) {
      console.error('Failed to log time:', err);
    }
  };

  const handleApproveTaskAction = async () => {
    if (!token || !taskId) return;
    try {
      const res = await fetch(`/api/tasks/${taskId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      });
      if (res.ok) {
        fetchTask();
        fetchHistory();
        onTaskUpdated();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to approve task.');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRequestChangesSubmit = async (reason: string) => {
    if (!token || !taskId) return;
    setIsSubmittingReviewAction(true);
    try {
      const res = await fetch(`/api/tasks/${taskId}/request-changes`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ rejectionReason: reason }),
      });
      if (res.ok) {
        setIsRequestChangesOpen(false);
        fetchTask();
        fetchHistory();
        onTaskUpdated();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to send the task for rework.');
      }
    } catch (err: any) {
      alert(err?.message || 'Failed to send the task for rework.');
    } finally {
      setIsSubmittingReviewAction(false);
    }
  };

  const handleSubmitForReviewAction = async () => {
    if (!token || !taskId) return;
    try {
      const res = await fetch(`/api/tasks/${taskId}/submit-review`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      });
      if (res.ok) {
        fetchTask();
        fetchHistory();
        onTaskUpdated();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to submit task for review.');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveField = async (updates: Partial<Task>) => {
    if (!token) return;
    if (isViewer) {
      alert('Viewers have read-only access and cannot edit tasks.');
      return;
    }
    if (updates.status) {
      const nextStatus = updates.status === 'completed' ? 'done' : updates.status;

      // Rule 4: If In Review -> In Progress by a reviewer (not self-recall), prompt mandatory rejection comment modal
      const isPrivilegedReviewer = canApproveTask(task);
      if (task?.status === 'in_review' && nextStatus === 'in_progress' && isPrivilegedReviewer && task?.assigneeId !== user?.id) {
        setIsRequestChangesOpen(true);
        return;
      }

      // Validate status transition against the 5 RBAC engine rules
      const check = checkTaskTransition(currentUserForPermissions, task || { id: taskId, status: 'todo' }, nextStatus);
      if (!check.allowed) {
        alert(check.error || 'Workflow Gated: You do not have permission to perform this task status transition.');
        return;
      }
    }
    try {
      const res = await fetch(`/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.task) {
          window.dispatchEvent(new CustomEvent('task-updated', { detail: data.task }));
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(errData.error || 'Failed to update task.');
      }
      fetchTask();
      onTaskUpdated();
    } catch (err) {
      console.error('Failed to update task:', err);
    }
  };

  // Checklist actions
  const handleAddChecklistItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newChecklistTitle.trim() || !token) return;

    try {
      await fetch(`/api/tasks/${taskId}/checklists`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ title: newChecklistTitle.trim() }),
      });
      setNewChecklistTitle('');
      fetchTask();
      onTaskUpdated();
    } catch (err) {
      console.error('Failed to add checklist item:', err);
    }
  };

  const handleToggleChecklist = async (chkId: string, completed: boolean) => {
    if (!token) return;
    try {
      await fetch(`/api/tasks/${taskId}/checklists/${chkId}`, {
        method: 'PUT',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ completed }),
      });
      fetchTask();
      onTaskUpdated();
    } catch (err) {
      console.error('Failed to toggle checklist:', err);
    }
  };

  const handleConvertChecklistToCard = async (chkId: string) => {
    if (!token) return;
    try {
      const res = await fetch(`/api/tasks/${taskId}/checklists/${chkId}/convert`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.task) {
          window.dispatchEvent(new CustomEvent('task-created', { detail: data.task }));
        }
        fetchTask();
        onTaskUpdated();
      }
    } catch (err) {
      console.error('Failed to convert checklist item to card:', err);
    }
  };

  const handleDeleteChecklist = async (chkId: string) => {
    if (!token) return;
    try {
      await fetch(`/api/tasks/${taskId}/checklists/${chkId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      fetchTask();
      onTaskUpdated();
    } catch (err) {
      console.error('Failed to delete checklist item:', err);
    }
  };

  // Comment actions
  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !token) return;

    try {
      await fetch(`/api/tasks/${taskId}/comments`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ content: newComment.trim() }),
      });
      setNewComment('');
      fetchTask();
      onTaskUpdated();
    } catch (err) {
      console.error('Failed to post comment:', err);
    }
  };

  const handleSaveCommentEdit = async (commentId: string) => {
    if (!token || !editingCommentText.trim()) return;
    try {
      const res = await fetch(`/api/tasks/${taskId}/comments/${commentId}`, {
        method: 'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ content: editingCommentText.trim() }),
      });
      if (res.ok) {
        setEditingCommentId(null);
        setEditingCommentText('');
        fetchTask();
      }
    } catch (err) {
      console.error('Failed to edit comment:', err);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!token) return;
    try {
      const res = await fetch(`/api/tasks/${taskId}/comments/${commentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchTask();
        onTaskUpdated();
      }
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const [isDraggingAttachments, setIsDraggingAttachments] = useState(false);
  const [previewFile, setPreviewFile] = useState<any | null>(null);

  // Multi-file attachment upload with drag & drop support
  const uploadFiles = async (files: FileList | File[]) => {
    if (!token || files.length === 0) return;
    setIsUploading(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('taskId', taskId);
      if (task?.projectId) formData.append('projectId', task.projectId);

      try {
        const res = await fetch('/api/files/upload', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: formData,
        });
        if (!res.ok) alert(await describeUploadFailure(res, file.name));
      } catch (err: any) {
        console.error('Attachment upload failed:', err);
        alert(`Could not upload "${file.name}": ${err.message || 'network error'}`);
      }
    }
    setIsUploading(false);
    fetchTask();
    fetchHistory();
    onTaskUpdated();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteAttachment = async (file: any) => {
    if (!window.confirm(`Remove "${file.originalName}" from this task? The file is deleted and cannot be restored.`)) return;
    try {
      const res = await fetch(`/api/files/${file.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        alert(body.error || `Could not remove "${file.originalName}" (HTTP ${res.status}).`);
        return;
      }
      fetchTask();
      fetchHistory();
      onTaskUpdated();
    } catch (err: any) {
      console.error('Failed to remove attachment:', err);
      alert(`Could not remove "${file.originalName}": ${err.message || 'network error'}`);
    }
  };

  const handleAttachmentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      uploadFiles(e.target.files);
    }
  };

  // Pasting a screenshot or copied image (Ctrl+V) while the task is open attaches it to the task,
  // like dropping it on the attachments area. Pasted text is left alone.
  const uploadFilesRef = useRef(uploadFiles);
  uploadFilesRef.current = uploadFiles;
  useEffect(() => {
    if (!isOpen) return;
    const handlePaste = (e: ClipboardEvent) => {
      const pasted = Array.from(e.clipboardData?.items || [])
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((f): f is File => Boolean(f));
      // Copied text (even with a picture alongside, e.g. from a spreadsheet) pastes as text as usual
      if (pasted.length === 0 || e.clipboardData?.getData('text/plain')) return;
      e.preventDefault();
      uploadFilesRef.current(
        pasted.map((f) =>
          f.name && f.name !== 'image.png'
            ? f
            : new File([f], `pasted-image-${Date.now()}.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`, { type: f.type })
        )
      );
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isOpen]);

  if (!isOpen) return null;

  const isWatching = user ? (task?.watchers || []).includes(user.id) : false;
  const totalChecklists = task?.checklists?.length || 0;
  const doneChecklists =
    task?.checklists?.filter((c: any) => c.completed).length || 0;
  const checklistPct =
    totalChecklists > 0 ? Math.round((doneChecklists / totalChecklists) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-surface-950/60 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer Container */}
      <div className="relative w-full max-w-2xl h-full bg-white dark:bg-surface-900 shadow-modal border-l border-surface-200 dark:border-surface-800 z-10 flex flex-col animate-slide-left font-sans">
        {/* Trello Card Cover Banner (if present) */}
        {task?.coverImageUrl ? (
          <div className="relative h-36 w-full bg-surface-900 shrink-0 overflow-hidden">
            <img
              src={task.coverImageUrl}
              alt="Card Cover"
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => handleSaveField({ coverImageUrl: null } as any)}
              className="absolute top-3 right-14 px-2.5 py-1 rounded-lg bg-black/60 hover:bg-black/80 text-white text-[11px] font-semibold backdrop-blur-xs"
            >
              Remove Cover
            </button>
          </div>
        ) : task?.coverColor ? (
          <div
            className="h-14 w-full shrink-0 transition-colors relative"
            style={{ backgroundColor: task.coverColor }}
          />
        ) : null}

        {/* Drawer Header with Unique IDs & View Tabs */}
        <div className="px-6 py-3.5 border-b border-surface-200 dark:border-surface-800 flex items-center justify-between shrink-0">
          <div className="flex items-center space-x-2 text-xs">
            {/* Unique Task ID with 1-Click Copy */}
            <div className="flex items-center space-x-1 px-2.5 py-1 rounded-xl bg-surface-100 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 shadow-sm">
              <span className="font-mono font-bold text-brand-600 dark:text-brand-400">
                {task?.code || task?.taskCode || `TSK-${task?.id}`}
              </span>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(
                    task?.code || task?.taskCode || `TSK-${task?.id}`
                  );
                  setIsCopiedId(true);
                  setTimeout(() => setIsCopiedId(false), 2000);
                }}
                className="p-0.5 text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 transition-colors"
                title="Copy unique card ID"
              >
                {isCopiedId ? (
                  <CheckCheck className="w-3.5 h-3.5 text-emerald-500" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
            </div>

            <span className="text-surface-300 font-mono">/</span>
            <span className="font-bold text-surface-600 dark:text-surface-300 font-mono">
              {task?.projectKey || 'BOARD'}
            </span>
            <span className="text-surface-300 font-mono">/</span>
            <span className="text-surface-500 truncate max-w-[130px] font-medium">
              {task?.projectName || 'Board'}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            {/* View Switcher: Details vs Audit Log */}
            <div className="flex p-0.5 bg-surface-100 dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700">
              <button
                type="button"
                onClick={() => setActiveTab('details')}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors ${
                  activeTab === 'details'
                    ? 'bg-white dark:bg-surface-700 text-brand-600 dark:text-brand-400 shadow-sm'
                    : 'text-surface-500 hover:text-surface-900 dark:hover:text-surface-100'
                }`}
              >
                Details
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('history')}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors flex items-center space-x-1 ${
                  activeTab === 'history'
                    ? 'bg-white dark:bg-surface-700 text-brand-600 dark:text-brand-400 shadow-sm'
                    : 'text-surface-500 hover:text-surface-900 dark:hover:text-surface-100'
                }`}
              >
                <History className="w-3.5 h-3.5" />
                <span>Ticket History</span>
                {historyLogs.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.2 rounded-full bg-brand-500 text-white text-[9px] font-bold">
                    {historyLogs.length}
                  </span>
                )}
              </button>
            </div>

            {/* Move to Bin / Archive Button (Soft delete) */}
            {!task?.isArchived && (
              <button
                type="button"
                onClick={handleArchiveTask}
                title="Archive Card to Bin"
                className="p-2 text-surface-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30 rounded-xl transition-colors border border-transparent hover:border-amber-200 dark:hover:border-amber-900"
              >
                <Archive className="w-4 h-4" />
              </button>
            )}

            <button
              onClick={onClose}
              className="p-1.5 text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Soft Delete / Bin Notice Banner */}
        {task?.isArchived && (
          <div className="px-6 py-2.5 bg-amber-500/15 border-b border-amber-500/30 flex items-center justify-between text-xs text-amber-800 dark:text-amber-300">
            <div className="flex items-center space-x-2">
              <Archive className="w-4 h-4 text-amber-600 shrink-0" />
              <span>
                <strong>This card is archived in the Bin.</strong>
                {task.archivedByName && ` Moved by ${task.archivedByName}.`}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={handleRestoreTask}
                className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold flex items-center space-x-1 shadow-sm transition-all text-xs"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Restore Card</span>
              </button>
              <button
                type="button"
                onClick={handlePermanentDeleteTask}
                className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold flex items-center space-x-1 shadow-sm transition-all text-xs"
                title="Permanently Delete Card"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        )}

        {loadError ? (
          <div className="p-8 text-center bg-surface-50 dark:bg-surface-850 m-6 rounded-2xl border border-surface-200 dark:border-surface-700 space-y-3">
            <ShieldAlert className="w-8 h-8 text-rose-500 mx-auto" />
            <h4 className="text-sm font-bold text-surface-900 dark:text-surface-100">
              Task Not Found
            </h4>
            <p className="text-xs text-surface-500 max-w-sm mx-auto">
              {loadError}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold rounded-xl bg-surface-200 dark:bg-surface-700 hover:bg-surface-300 dark:hover:bg-surface-600 transition-colors"
            >
              Close
            </button>
          </div>
        ) : isLoading || !task ? (
          <div className="p-12 text-center text-xs text-surface-400 flex flex-col items-center justify-center space-y-3">
            <div className="w-6 h-6 border-2 border-brand-500/20 border-t-brand-500 rounded-full animate-spin" />
            <span>Loading card details...</span>
          </div>
        ) : activeTab === 'history' ? (
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-surface-200 dark:border-surface-800">
              <div>
                <h4 className="font-bold text-sm text-surface-900 dark:text-surface-100 flex items-center space-x-2">
                  <History className="w-4 h-4 text-brand-500" />
                  <span>Activity History & Accountability Log</span>
                </h4>
                <p className="text-xs text-surface-500 mt-0.5">
                  Complete chronological audit record of all changes, completions, edits, and archiving.
                </p>
              </div>
              <button
                type="button"
                onClick={fetchHistory}
                className="px-2.5 py-1 text-xs font-bold text-brand-600 hover:text-brand-700 rounded-lg hover:bg-brand-50 dark:hover:bg-brand-950/40"
              >
                Refresh
              </button>
            </div>

            {isLoadingHistory ? (
              <div className="text-center py-8 text-xs text-surface-400">Loading audit history...</div>
            ) : historyLogs.length === 0 ? (
              <div className="text-center py-10 bg-surface-50 dark:bg-surface-800/40 rounded-2xl border border-surface-200/60 dark:border-surface-700/60 p-6 space-y-2">
                <ShieldAlert className="w-8 h-8 text-surface-400 mx-auto" />
                <h5 className="font-bold text-xs text-surface-700 dark:text-surface-300">
                  Initial Card Record Active
                </h5>
                <p className="text-xs text-surface-500 max-w-sm mx-auto">
                  Created by {task.creatorName || 'Team Member'} on{' '}
                  {new Date(task.createdAt).toLocaleString()}. All subsequent changes will be tracked here.
                </p>
              </div>
            ) : (
              <div className="relative pl-6 space-y-4 before:absolute before:left-2.5 before:top-3 before:bottom-3 before:w-0.5 before:bg-surface-200 dark:before:bg-surface-700">
                {historyLogs.map((log) => {
                  let badgeColor = 'bg-indigo-600';
                  let label = 'CREATED';
                  let detailText = log.metadata?.details || '';

                  if (log.action === 'TASK_COMPLETE') {
                    badgeColor = 'bg-emerald-600';
                    label = 'COMPLETED';
                    detailText =
                      detailText || `Task completed by ${log.metadata?.completedBy || log.userName}`;
                  } else if (log.action === 'STATUS_CHANGED') {
                    badgeColor = 'bg-blue-600';
                    label = 'STATUS';
                    detailText =
                      detailText || `Status changed to ${(log.metadata?.to || '').toUpperCase()}`;
                  } else if (log.action === 'ASSIGNEE_CHANGED') {
                    badgeColor = 'bg-purple-600';
                    label = 'REASSIGNED';
                    detailText = detailText || `Assigned to ${log.metadata?.to}`;
                  } else if (log.action === 'PRIORITY_CHANGED') {
                    badgeColor = 'bg-amber-600';
                    label = 'PRIORITY';
                    detailText =
                      detailText || `Priority set to ${(log.metadata?.to || '').toUpperCase()}`;
                  } else if (log.action === 'DUE_DATE_CHANGED') {
                    badgeColor = 'bg-cyan-600';
                    label = 'DUE DATE';
                    detailText = detailText || `Due date set to ${log.metadata?.to}`;
                  } else if (log.action === 'TITLE_CHANGED') {
                    badgeColor = 'bg-slate-600';
                    label = 'TITLE';
                    detailText = detailText || `Title updated`;
                  } else if (log.action === 'TASK_ARCHIVE') {
                    badgeColor = 'bg-amber-600';
                    label = 'MOVED TO BIN';
                    detailText =
                      detailText || `Moved to Bin by ${log.metadata?.archivedBy || log.userName}`;
                  } else if (log.action === 'TASK_RESTORE') {
                    badgeColor = 'bg-blue-600';
                    label = 'RESTORED';
                    detailText =
                      detailText || `Restored by ${log.metadata?.restoredBy || log.userName}`;
                  } else if (log.action === 'TASK_REOPEN') {
                    badgeColor = 'bg-orange-600';
                    label = 'REOPENED';
                    detailText =
                      detailText || `Reopened by ${log.metadata?.reopenedBy || log.userName}`;
                  } else if (log.action === 'ATTACHMENT_ADDED') {
                    badgeColor = 'bg-teal-600';
                    label = 'ATTACHMENT';
                    detailText = detailText || 'Attachment added';
                  } else if (log.action === 'TASK_CREATE') {
                    badgeColor = 'bg-indigo-600';
                    label = 'CREATED';
                    detailText =
                      detailText || `Status changed to ${task.status?.toUpperCase() || 'TODO'}`;
                  }

                  return (
                    <div key={log.id} className="relative">
                      <span
                        className={`absolute -left-6 top-2 w-3 h-3 rounded-full ${badgeColor} ring-4 ring-white dark:ring-surface-900`}
                      />
                      <div className="p-3.5 rounded-2xl bg-surface-50 dark:bg-surface-800/60 border border-surface-200/60 dark:border-surface-700/60 text-xs space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Avatar src={log.userAvatar} name={log.userName} size="xs" />
                            <span className="font-bold text-surface-900 dark:text-surface-100">
                              {log.userName}
                            </span>
                            <RoleBadge role={log.userRole || 'member'} />
                          </div>
                          <span className="text-[10px] text-surface-400 font-medium">
                            {new Date(log.createdAt).toLocaleString([], {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>

                        <div className="flex items-center space-x-2.5 pt-0.5">
                          <span
                            className={`text-[9px] font-black px-2.5 py-0.5 rounded-full text-white uppercase tracking-wider ${badgeColor}`}
                          >
                            {label}
                          </span>
                          <span className="text-surface-700 dark:text-surface-200 font-medium">
                            {detailText}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* In Review / Awaiting Approval Banner */}
            {(task.status === 'in_review' || task.reviewStatus === 'awaiting_approval') && (
              <div className="p-3.5 bg-amber-50 dark:bg-amber-950/25 border border-amber-200 dark:border-amber-800/60 rounded-2xl space-y-2.5 text-xs text-amber-900 dark:text-amber-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse shrink-0" />
                    <span>
                      <strong>Awaiting Approval:</strong> Submitted for review
                      {task.submittedForReviewAt &&
                        ` on ${new Date(task.submittedForReviewAt).toLocaleString([], {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}`}
                      {task.assignorName && ` • Assignor: ${task.assignorName}`}
                    </span>
                  </div>
                  <span className="text-[10px] uppercase font-black px-2.5 py-0.5 rounded-full bg-amber-500 text-white shadow-sm">
                    In Review
                  </span>
                </div>

                {/* Approver Action Buttons */}
                {canApproveTask(task) && (
                  <div className="pt-2 border-t border-amber-200/60 dark:border-amber-800/40 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold text-amber-800 dark:text-amber-300">
                      Approval Authority: You can approve this task into Completed or send it for rework.
                    </span>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={handleApproveTaskAction}
                        className="px-3 py-1.5 text-xs rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold transition-colors flex items-center space-x-1 shadow-sm"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Approve</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsRequestChangesOpen(true)}
                        className="px-3 py-1.5 text-xs rounded-xl bg-rose-50 dark:bg-rose-900/30 hover:bg-rose-100 dark:hover:bg-rose-800/40 text-rose-700 dark:text-rose-300 font-bold border border-rose-200 dark:border-rose-800 transition-colors flex items-center space-x-1 whitespace-nowrap"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span>Send for Rework</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Changes Requested Banner */}
            {task.reviewStatus === 'changes_requested' && (
              <div className="p-3.5 bg-rose-50 dark:bg-rose-950/25 border border-rose-200 dark:border-rose-800/60 rounded-2xl space-y-2 text-xs text-rose-900 dark:text-rose-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-500 shrink-0" />
                    <span>
                      <strong>Rework</strong> {task.revisionCount ? `(Revision #${task.revisionCount})` : ''}
                    </span>
                  </div>
                  <span className="text-[10px] uppercase font-black px-2.5 py-0.5 rounded-full bg-rose-600 text-white shadow-sm">
                    Revisions Needed
                  </span>
                </div>
                <p className="text-[11px] text-rose-700 dark:text-rose-300">
                  This task was sent back to In Progress by the reviewer. Please review feedback in the comments and resubmit when ready.
                </p>
                {task.status === 'in_progress' && (
                  <div className="pt-2 border-t border-rose-200/60 dark:border-rose-800/40 flex justify-end">
                    <button
                      type="button"
                      onClick={handleSubmitForReviewAction}
                      className="px-3 py-1.5 text-xs rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold transition-colors shadow-sm"
                    >
                      Resubmit for Review
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Completion Accountability Banner & Team Lead Review/Reassign */}
            {(task.status === 'done' || task.status === 'completed') && (
              <div className="p-3.5 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/60 rounded-2xl space-y-2.5 text-xs text-emerald-900 dark:text-emerald-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>
                      {task.approvedByName ? (
                        <>
                          Approved & Completed by <strong>{task.approvedByName}</strong>
                        </>
                      ) : (
                        <>
                          Marked as Complete by{' '}
                          <strong>
                            {task.completedByName || task.assigneeName || 'Team Member'}
                          </strong>
                        </>
                      )}
                      {task.completedAt &&
                        ` • ${new Date(task.completedAt).toLocaleString([], {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}`}
                    </span>
                  </div>
                  <span className="text-[10px] uppercase font-black px-2.5 py-0.5 rounded-full bg-emerald-600 text-white shadow-sm">
                    Verified
                  </span>
                </div>

                {/* Team Lead Reassign / Review Controls */}
                {(isWorkspaceAdmin || isProjectLead) && (
                  <div className="pt-2 border-t border-emerald-200/60 dark:border-emerald-800/40 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold text-emerald-800 dark:text-emerald-300">
                      Team Lead: Reassign card or reopen:
                    </span>
                    <div className="flex items-center space-x-2">
                      <select
                        defaultValue=""
                        onChange={(e) => {
                          const newAssigneeId = e.target.value;
                          if (newAssigneeId) {
                            handleSaveField({
                              assigneeId: newAssigneeId,
                              status: 'in_progress',
                            });
                          }
                        }}
                        className="px-2.5 py-1 text-xs rounded-xl bg-white dark:bg-surface-800 border border-emerald-300 dark:border-emerald-700 text-surface-900 dark:text-surface-100 font-medium focus:outline-none"
                      >
                        <option value="">Reassign Card To...</option>
                        {teamMembers.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.fullName} ({m.role})
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => handleSaveField({ status: 'in_progress' })}
                        className="px-2.5 py-1 text-xs rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold transition-colors"
                      >
                        Reopen to In Progress
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Colored Labels Pills Row */}
            {task.coloredLabels && task.coloredLabels.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                {task.coloredLabels.map((lbl: CardLabel, idx: number) => {
                  const paletteMatch = TRELLO_LABEL_PALETTE.find(
                    (p) => p.color === lbl.color
                  );
                  return (
                    <span
                      key={`${lbl.id || lbl.color}-${idx}`}
                      onClick={() => setActivePopover('labels')}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-bold text-white cursor-pointer hover:opacity-90 transition-opacity shadow-2xs ${
                        paletteMatch ? paletteMatch.bg : 'bg-brand-600'
                      }`}
                    >
                      {lbl.name || paletteMatch?.name || 'Label'}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Title Input with 1-Click Complete Checkbox */}
            <div className="flex items-start gap-2.5">
              <button
                type="button"
                onClick={() => {
                  if (isViewer || isGuest) {
                    alert(isViewer ? 'Viewers have read-only access.' : 'Guests cannot change card status.');
                    return;
                  }
                  const nextStatus =
                    status === 'completed' || status === 'done' ? 'in_progress' : 'completed';
                  if (nextStatus === 'completed' && !canApproveTask(task)) {
                    alert('Workflow Gated: Only Team Leads, Workspace Admins, or the task Assignor can approve tasks into Completed.');
                    return;
                  }
                  if ((status === 'completed' || status === 'done') && nextStatus !== 'completed' && !isWorkspaceAdmin && !isProjectLead) {
                    alert('Only Team Leads or Workspace Admins can reopen completed tasks.');
                    return;
                  }
                  setStatus(nextStatus);
                  handleSaveField({ status: nextStatus });
                }}
                className={`mt-1.5 w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
                  status === 'completed' || status === 'done'
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : 'border-surface-300 dark:border-surface-600 hover:border-brand-500'
                }`}
                title="Toggle card complete"
              >
                {(status === 'completed' || status === 'done') && (
                  <CheckCircle2 className="w-3.5 h-3.5" />
                )}
              </button>
              <input
                type="text"
                value={title}
                disabled={isViewer}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => {
                  if (title.trim() && title !== task.title) {
                    handleSaveField({ title: title.trim() });
                  }
                }}
                className="flex-1 text-lg font-bold text-surface-900 dark:text-surface-100 bg-transparent border-none focus:outline-none focus:ring-1 focus:ring-brand-500/50 rounded-lg px-1 py-0.5 disabled:opacity-70"
              />
            </div>

            {/* Trello Quick Action Toolbar */}
            <div className="relative flex flex-wrap items-center gap-1.5 pb-2 border-b border-surface-100 dark:border-surface-800">
              <button
                type="button"
                onClick={() =>
                  setActivePopover(activePopover === 'labels' ? null : 'labels')
                }
                className="px-2.5 py-1.5 rounded-xl bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-xs font-semibold text-surface-700 dark:text-surface-200 flex items-center gap-1.5 transition-colors"
              >
                <Tag className="w-3.5 h-3.5 text-brand-500" />
                <span>Labels</span>
              </button>

              <button
                type="button"
                onClick={() =>
                  setActivePopover(activePopover === 'members' ? null : 'members')
                }
                className="px-2.5 py-1.5 rounded-xl bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-xs font-semibold text-surface-700 dark:text-surface-200 flex items-center gap-1.5 transition-colors"
              >
                <Users className="w-3.5 h-3.5 text-indigo-500" />
                <span>Members ({(task.assignees || []).length})</span>
              </button>

              <button
                type="button"
                onClick={() =>
                  setActivePopover(activePopover === 'cover' ? null : 'cover')
                }
                className="px-2.5 py-1.5 rounded-xl bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-xs font-semibold text-surface-700 dark:text-surface-200 flex items-center gap-1.5 transition-colors"
              >
                <Palette className="w-3.5 h-3.5 text-emerald-500" />
                <span>Cover</span>
              </button>

              <button
                type="button"
                onClick={handleToggleWatch}
                className={`px-2.5 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                  isWatching
                    ? 'bg-brand-600 text-white'
                    : 'bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-200'
                }`}
              >
                <Eye className="w-3.5 h-3.5" />
                <span>{isWatching ? 'Watching' : 'Watch'}</span>
              </button>

              <button
                type="button"
                onClick={() => setActivePopover(activePopover === 'move' ? null : 'move')}
                className="px-2.5 py-1.5 rounded-xl bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-xs font-semibold text-surface-700 dark:text-surface-200 flex items-center gap-1.5 transition-colors"
              >
                <ArrowRightLeft className="w-3.5 h-3.5 text-amber-500" />
                <span>Move</span>
              </button>

              <button
                type="button"
                onClick={handleDuplicateCard}
                className="px-2.5 py-1.5 rounded-xl bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-xs font-semibold text-surface-700 dark:text-surface-200 flex items-center gap-1.5 transition-colors"
              >
                <Copy className="w-3.5 h-3.5 text-purple-500" />
                <span>Copy Card</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  const url = `${window.location.origin}/?projectId=${task.projectId}&taskId=${task.id}`;
                  navigator.clipboard.writeText(url);
                  setIsCopiedLink(true);
                  setTimeout(() => setIsCopiedLink(false), 2000);
                }}
                className="px-2.5 py-1.5 rounded-xl bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-xs font-semibold text-surface-700 dark:text-surface-200 flex items-center gap-1.5 transition-colors"
              >
                <Share2 className="w-3.5 h-3.5 text-cyan-500" />
                <span>{isCopiedLink ? 'Copied!' : 'Share'}</span>
              </button>

              {/* Labels Popover */}
              {activePopover === 'labels' && (
                <div className="absolute left-0 top-full mt-2 w-72 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-2xl shadow-elevated z-30 p-3.5 space-y-2.5 text-xs">
                  <div className="flex items-center justify-between font-bold text-surface-800 dark:text-surface-100">
                    <span>Card Labels</span>
                    <button
                      type="button"
                      onClick={() => setActivePopover(null)}
                      className="text-surface-400 hover:text-surface-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {TRELLO_LABEL_PALETTE.map((item) => {
                      const active = (task.coloredLabels || []).some(
                        (l: CardLabel) => l.color === item.color
                      );
                      const existing = (task.coloredLabels || []).find(
                        (l: CardLabel) => l.color === item.color
                      );
                      return (
                        <div key={item.color} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={active}
                            onChange={() => handleToggleColoredLabel(item)}
                            className="rounded border-surface-300 text-brand-600"
                          />
                          <input
                            type="text"
                            value={
                              customLabelNames[item.color] !== undefined
                                ? customLabelNames[item.color]
                                : existing?.name || item.name
                            }
                            onChange={(e) =>
                              setCustomLabelNames((prev) => ({
                                ...prev,
                                [item.color]: e.target.value,
                              }))
                            }
                            onBlur={() => {
                              if (active && customLabelNames[item.color] !== undefined) {
                                const updated = (task.coloredLabels || []).map((l: CardLabel) =>
                                  l.color === item.color
                                    ? { ...l, name: customLabelNames[item.color] }
                                    : l
                                );
                                handleSaveField({ coloredLabels: updated } as any);
                              }
                            }}
                            className={`flex-1 px-2.5 py-1 rounded-lg font-bold text-xs focus:outline-none ${item.bg}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Cover Picker Popover */}
              {activePopover === 'cover' && (
                <div className="absolute left-20 top-full mt-2 w-64 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-2xl shadow-elevated z-30 p-3.5 space-y-3 text-xs">
                  <div className="flex items-center justify-between font-bold text-surface-800 dark:text-surface-100">
                    <span>Card Cover</span>
                    <button
                      type="button"
                      onClick={() => setActivePopover(null)}
                      className="text-surface-400 hover:text-surface-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {TRELLO_COVER_COLORS.filter((c) => c.hex).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => handleSaveField({ coverColor: c.hex } as any)}
                        className={`h-7 rounded-lg transition-transform ${
                          task.coverColor === c.hex
                            ? 'ring-2 ring-offset-1 ring-brand-500 scale-105'
                            : 'hover:scale-105'
                        }`}
                        style={{ backgroundColor: c.hex }}
                        title={c.label}
                      />
                    ))}
                  </div>
                  {(task.coverColor || task.coverImageUrl) && (
                    <button
                      type="button"
                      onClick={() =>
                        handleSaveField({ coverColor: null, coverImageUrl: null } as any)
                      }
                      className="w-full py-1.5 rounded-lg bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-200 font-semibold"
                    >
                      Remove Cover
                    </button>
                  )}
                </div>
              )}

              {/* Multi-Members Picker Popover */}
              {activePopover === 'members' && (
                <div className="absolute left-12 top-full mt-2 w-64 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-2xl shadow-elevated z-30 p-3.5 space-y-2.5 text-xs">
                  <div className="flex items-center justify-between font-bold text-surface-800 dark:text-surface-100">
                    <span>Assign Card Members</span>
                    <button
                      type="button"
                      onClick={() => setActivePopover(null)}
                      className="text-surface-400 hover:text-surface-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {displayedScopedMembers.map((m) => {
                      const assigned = (task.assignees || []).some(
                        (a: any) => a.id === m.id
                      );
                      return (
                        <label
                          key={m.id}
                          className="flex items-center justify-between p-1.5 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-700/50 cursor-pointer"
                        >
                          <div className="flex items-center space-x-2">
                            <Avatar src={m.avatarUrl} name={m.fullName} size="xs" />
                            <span className="font-medium text-surface-800 dark:text-surface-200">
                              {m.fullName}
                            </span>
                          </div>
                          <input
                            type="checkbox"
                            checked={assigned}
                            onChange={() => handleToggleMemberAssignment(m.id)}
                            className="rounded border-surface-300 text-brand-600"
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Move Card Popover */}
              {activePopover === 'move' && (
                <form
                  onSubmit={handleMoveCard}
                  className="absolute left-28 top-full mt-2 w-72 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-2xl shadow-elevated z-30 p-3.5 space-y-3 text-xs"
                >
                  <div className="flex items-center justify-between font-bold text-surface-800 dark:text-surface-100">
                    <span>Move Card</span>
                    <button
                      type="button"
                      onClick={() => setActivePopover(null)}
                      className="text-surface-400 hover:text-surface-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase text-surface-400 mb-1">
                      Destination Board
                    </label>
                    <select
                      value={moveTargetProjectId}
                      onChange={(e) => setMoveTargetProjectId(e.target.value)}
                      className="w-full px-2.5 py-1.5 rounded-lg bg-surface-50 dark:bg-surface-900 border border-surface-200 dark:border-surface-700"
                    >
                      {allProjects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase text-surface-400 mb-1">
                      Target List / Stage
                    </label>
                    <select
                      value={moveTargetStatus}
                      onChange={(e) => setMoveTargetStatus(e.target.value as TaskStatus)}
                      className="w-full px-2.5 py-1.5 rounded-lg bg-surface-50 dark:bg-surface-900 border border-surface-200 dark:border-surface-700"
                    >
                      <option value="todo">To Do</option>
                      <option value="in_progress">In Progress</option>
                      <option value="in_review">In Review</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold"
                  >
                    Move Card
                  </button>
                </form>
              )}
            </div>

            {/* Properties Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3.5 sm:p-4 rounded-2xl bg-surface-50 dark:bg-surface-800/50 border border-surface-200/60 dark:border-surface-700/60 text-xs">
              {/* Status */}
              <div>
                <label className="block text-[10px] uppercase font-bold text-surface-400 mb-1">
                  Status / Stage
                </label>
                <select
                  value={status === 'completed' ? 'done' : status}
                  disabled={isViewer || isGuest}
                  onChange={(e) => {
                    const s = e.target.value as TaskStatus;
                    setStatus(s);
                    handleSaveField({ status: s });
                  }}
                  className="w-full text-xs font-semibold px-3 py-2 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none min-h-[42px] disabled:opacity-50"
                >
                  <option value="backlog">Backlog</option>
                  <option value="todo">To Do</option>
                  <option value="in_progress">In Progress</option>
                  <option value="in_review">In Review</option>
                  <option value="done" disabled={!canApproveTask(task)}>
                    Completed {!canApproveTask(task) ? '(Locked)' : ''}
                  </option>
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-[10px] uppercase font-bold text-surface-400 mb-1">
                  Priority
                </label>
                <select
                  value={priority}
                  disabled={isViewer}
                  onChange={(e) => {
                    const p = e.target.value as PriorityLevel;
                    setPriority(p);
                    handleSaveField({ priority: p });
                  }}
                  className="w-full text-xs font-semibold px-3 py-2 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none min-h-[42px] disabled:opacity-50"
                >
                  <option value="urgent">⚡ Urgent</option>
                  <option value="high">▲ High</option>
                  <option value="medium">● Medium</option>
                  <option value="low">▼ Low</option>
                </select>
              </div>

              {/* Project */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[10px] uppercase font-bold text-surface-400">
                    Project
                  </label>
                  {task.projectId && (
                    <button
                      type="button"
                      onClick={() => setIsViewingProjectMembers(true)}
                      className="inline-flex items-center space-x-1 text-[10px] font-semibold text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
                      title="Show existing members of this project"
                    >
                      <Users className="w-3 h-3" />
                      <span>Existing Members ({scopedMembers.length})</span>
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={task.projectId || ''}
                    disabled={isViewer || isGuest}
                    onChange={(e) => {
                      const nextProjectId = e.target.value;
                      if (nextProjectId !== task.projectId) {
                        setMoveTargetProjectId(nextProjectId);
                        handleSaveField({ projectId: nextProjectId || null });
                      }
                    }}
                    className="flex-1 text-xs font-semibold px-3 py-2 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none min-h-[42px] disabled:opacity-50"
                  >
                    <option value="">No Project (All Team Members)</option>
                    {allProjects.map((p) => (
                      <option key={p.id} value={p.id}>
                        [{p.key}] {p.name}
                      </option>
                    ))}
                  </select>

                  {task.projectId && (
                    <button
                      type="button"
                      onClick={() => setIsViewingProjectMembers(true)}
                      className="px-2.5 py-2 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors shrink-0 flex items-center space-x-1 min-h-[42px]"
                      title="Show existing members of this project"
                    >
                      <Users className="w-4 h-4 text-brand-600 dark:text-brand-400" />
                      <span className="text-[11px] font-semibold">({scopedMembers.length})</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Assignor (The person doing the assigning) */}
              <div>
                <label className="block text-[10px] uppercase font-bold text-surface-400 mb-1">
                  Assignor (Assigned By)
                </label>
                <div className="flex items-center space-x-2.5 px-3 py-2 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 min-h-[42px]">
                  <Avatar
                    src={task.assignorAvatar || task.creatorAvatar}
                    name={task.assignorName || task.creatorName || (task.assignorId === user?.id ? user?.fullName : 'Assignor')}
                    size="xs"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-surface-900 dark:text-surface-100 truncate flex items-center gap-1">
                      <span>{task.assignorName || task.creatorName || (task.assignorId === user?.id ? user?.fullName : 'Creator')}</span>
                      {(task.assignorId === user?.id || task.creatorId === user?.id) && (
                        <span className="text-[10px] text-brand-600 dark:text-brand-400 font-medium">(You)</span>
                      )}
                    </div>
                    <div className="text-[10px] text-surface-400 truncate">
                      {task.assignorRole ? `Assignor • ${task.assignorRole}` : 'Task Assignor'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Assignee (The person assigned to the card) */}
              <div>
                <label className="block text-[10px] uppercase font-bold text-surface-400 mb-1">
                  Assignee (Assigned To)
                </label>
                <select
                  value={assigneeId || ''}
                  onChange={(e) => {
                    const val = e.target.value || null;
                    setAssigneeId(val);
                    handleSaveField({ assigneeId: val });
                  }}
                  className="w-full text-xs font-semibold px-3 py-2 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none min-h-[42px]"
                >
                  <option value="">Unassigned</option>
                  {displayedScopedMembers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.fullName} {m.id === user?.id ? '(You)' : ''} ({m.role || 'Member'})
                    </option>
                  ))}
                </select>
              </div>

              {/* Start Date */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[10px] uppercase font-bold text-surface-400">
                    Start Date
                  </label>
                  {startDate && (
                    <button
                      type="button"
                      onClick={() => {
                        setStartDate('');
                        handleSaveField({ startDate: null } as any);
                      }}
                      className="text-[10px] text-surface-400 hover:text-rose-500 font-semibold"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    const nextVal = e.target.value;
                    setStartDate(nextVal);
                    handleSaveField({ startDate: nextVal || null } as any);
                  }}
                  className="w-full text-xs px-3 py-2 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-pointer min-h-[42px]"
                />
              </div>

              {/* Due Date */}
              <div className="sm:col-span-2">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <label className="block text-[10px] uppercase font-bold text-surface-400">
                    Due Date
                  </label>
                  <div className="flex items-center gap-1.5">
                    {[
                      { label: 'Today', days: 0 },
                      { label: 'Tomorrow', days: 1 },
                      { label: '+7 Days', days: 7 },
                    ].map((preset) => (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() => {
                          const d = new Date();
                          d.setDate(d.getDate() + preset.days);
                          const val = d.toISOString().split('T')[0];
                          setDueDate(val);
                          handleSaveField({ dueDate: val });
                        }}
                        className="px-2 py-0.5 rounded-md bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-[10px] font-semibold text-surface-600 dark:text-surface-300 hover:border-brand-500 hover:text-brand-600 transition-colors"
                      >
                        {preset.label}
                      </button>
                    ))}
                    {dueDate && (
                      <button
                        type="button"
                        onClick={() => {
                          setDueDate('');
                          handleSaveField({ dueDate: null });
                        }}
                        className="px-2 py-0.5 rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/50 text-[10px] font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-100 transition-colors"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => {
                    const nextVal = e.target.value;
                    setDueDate(nextVal);
                    handleSaveField({ dueDate: nextVal || null });
                  }}
                  className="w-full text-xs px-3 py-2 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-pointer min-h-[42px]"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-surface-400 mb-1.5">
                Description & Specifications
              </label>
              <DescriptionEditor
                rows={4}
                value={description}
                onChange={setDescription}
                onBlur={() => {
                  if (description !== task.description) {
                    handleSaveField({ description });
                  }
                }}
                placeholder="Add rich details, background context, or links to specifications..."
                className="w-full p-3 text-xs rounded-xl bg-surface-50 dark:bg-surface-800/40 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500 leading-relaxed"
              />
            </div>

            {/* Checklists Section with Progress Bar & Convert to Card */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-surface-400 flex items-center">
                  <CheckCircle2 className="w-4 h-4 mr-1.5 text-brand-500" />
                  Checklist ({doneChecklists}/{totalChecklists})
                </span>
                <div className="flex items-center gap-2">
                  {doneChecklists > 0 && (
                    <button
                      type="button"
                      onClick={() => setHideCompletedChecklists((prev) => !prev)}
                      className="text-[11px] font-semibold text-surface-500 hover:text-brand-600"
                    >
                      {hideCompletedChecklists
                        ? `Show checked (${doneChecklists})`
                        : 'Hide checked items'}
                    </button>
                  )}
                  {totalChecklists > 0 && (
                    <span className="text-xs font-bold text-brand-600 dark:text-brand-400">
                      {checklistPct}%
                    </span>
                  )}
                </div>
              </div>

              {totalChecklists > 0 && (
                <div className="w-full bg-surface-100 dark:bg-surface-800 h-1.5 rounded-full overflow-hidden mb-3">
                  <div
                    className={`h-full rounded-full transition-all ${
                      checklistPct === 100 ? 'bg-emerald-500' : 'bg-brand-500'
                    }`}
                    style={{ width: `${checklistPct}%` }}
                  />
                </div>
              )}

              <div className="space-y-1.5 mb-2">
                {task.checklists
                  ?.filter((item: any) => !hideCompletedChecklists || !item.completed)
                  .map((item: any) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-2 rounded-xl bg-surface-50 dark:bg-surface-800/50 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors group"
                    >
                      <label className="flex items-center space-x-2.5 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={item.completed}
                          onChange={(e) =>
                            handleToggleChecklist(item.id, e.target.checked)
                          }
                          className="rounded border-surface-300 text-brand-600 focus:ring-brand-500 w-4 h-4"
                        />
                        <span
                          className={`text-xs ${
                            item.completed
                              ? 'line-through text-surface-400 dark:text-surface-500'
                              : 'text-surface-800 dark:text-surface-200'
                          }`}
                        >
                          {item.title}
                        </span>
                      </label>
                      <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => handleConvertChecklistToCard(item.id)}
                          className="p-1 text-surface-400 hover:text-brand-600"
                          title="Convert checklist item to card"
                        >
                          <CornerUpRight className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteChecklist(item.id)}
                          className="p-1 text-surface-400 hover:text-rose-500"
                          aria-label="Delete checklist item"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
              </div>

              <form onSubmit={handleAddChecklistItem} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={newChecklistTitle}
                  onChange={(e) => setNewChecklistTitle(e.target.value)}
                  placeholder="Add checklist item..."
                  className="flex-1 px-3 py-1.5 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none"
                />
                <button
                  type="submit"
                  className="px-3 py-1.5 bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-200 hover:bg-brand-600 hover:text-white rounded-xl text-xs font-semibold transition-colors"
                >
                  Add
                </button>
              </form>
            </div>

            {/* Attachments Section with Drag & Drop + Make Cover */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-surface-400 flex items-center">
                  <Paperclip className="w-4 h-4 mr-1.5 text-brand-500" />
                  Attachments {task.files && task.files.length > 0 && `(${task.files.length})`}
                </span>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="text-xs font-semibold text-brand-600 hover:text-brand-700 flex items-center"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  {isUploading ? 'Uploading...' : 'Add file'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleAttachmentUpload}
                  className="hidden"
                />
              </div>

              {/* Drag & Drop Zone */}
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDraggingAttachments(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setIsDraggingAttachments(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDraggingAttachments(false);
                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    uploadFiles(e.dataTransfer.files);
                  }
                }}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-3 mb-2.5 text-center cursor-pointer transition-all ${
                  isDraggingAttachments
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-950/30 ring-2 ring-brand-500/20'
                    : 'border-surface-200 dark:border-surface-700/80 bg-surface-50/50 dark:bg-surface-850 hover:border-brand-400'
                }`}
              >
                <div className="flex items-center justify-center space-x-2 text-xs text-surface-500">
                  <UploadCloud
                    className={`w-4 h-4 ${
                      isDraggingAttachments ? 'text-brand-600' : 'text-surface-400'
                    }`}
                  />
                  <span className="font-medium">
                    {isDraggingAttachments
                      ? 'Drop files to attach'
                      : 'Drag & drop files here, or click to upload'}
                  </span>
                </div>
              </div>

              {task.files && task.files.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {task.files.map((file: any) => {
                    const isImage = /\.(png|jpe?g|gif|webp|svg)$/i.test(
                      file.originalName || ''
                    );
                    const downloadUrl = `/api/files/${file.id}/download`;
                    return (
                      <div
                        key={file.id}
                        onClick={() => setPreviewFile(file)}
                        className="p-2.5 rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/40 flex items-center justify-between gap-2 hover:border-brand-300 dark:hover:border-brand-700 hover:bg-brand-50/20 dark:hover:bg-brand-950/20 cursor-pointer transition-all group"
                      >
                        <div className="flex items-center space-x-2 truncate">
                          <FileText className="w-4 h-4 text-brand-500 shrink-0 group-hover:scale-110 transition-transform" />
                          <span
                            className="text-xs font-medium text-surface-800 dark:text-surface-200 truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors"
                            title={file.originalName}
                          >
                            {file.originalName}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => setPreviewFile(file)}
                            className="p-1 text-surface-400 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-950/40 rounded transition-colors"
                            title="Preview Document"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          {isImage && (
                            <button
                              type="button"
                              onClick={() =>
                                handleSaveField({ coverImageUrl: downloadUrl } as any)
                              }
                              className="p-1 text-surface-400 hover:text-brand-600 rounded transition-colors"
                              title="Make Card Cover"
                            >
                              <ImageIcon className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <a
                            href={downloadUrl}
                            download={file.originalName}
                            className="p-1 text-surface-400 hover:text-brand-600 rounded transition-colors"
                            title="Download"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </a>
                          {/* The uploader or an admin can remove an attachment (the server enforces the same) */}
                          {user && (file.uploadedBy === user.id || user.role === 'admin') && (
                            <button
                              type="button"
                              onClick={() => handleDeleteAttachment(file)}
                              className="p-1 text-surface-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded transition-colors"
                              title="Remove attachment"
                              aria-label={`Remove ${file.originalName}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {/* Time Tracking & Financial Hours */}
            <div className="pt-4 border-t border-surface-200 dark:border-surface-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-surface-400 flex items-center">
                  <Timer className="w-4 h-4 mr-1.5 text-brand-500" />
                  Time Tracking & Billable Hours
                </span>
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-bold text-surface-700 dark:text-surface-300">
                    Total: {timeEntries.reduce((sum, e) => sum + e.hours, 0).toFixed(1)}h
                  </span>
                  <button
                    onClick={() => setIsLoggingTime(!isLoggingTime)}
                    className="text-xs font-semibold text-brand-600 hover:text-brand-700 flex items-center"
                  >
                    <Plus className="w-3.5 h-3.5 mr-0.5" />
                    {isLoggingTime ? 'Cancel' : 'Log Time'}
                  </button>
                </div>
              </div>

              {/* Time Logging Form */}
              {isLoggingTime && (
                <form
                  onSubmit={handleLogTime}
                  className="p-3 mb-3 rounded-2xl bg-surface-50 dark:bg-surface-800/60 border border-surface-200 dark:border-surface-700 space-y-2.5"
                >
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[10px] font-bold text-surface-400 uppercase mb-0.5">
                        Hours
                      </label>
                      <input
                        type="number"
                        step="0.25"
                        min="0.25"
                        value={logHours}
                        onChange={(e) => setLogHours(e.target.value)}
                        className="w-full p-1.5 text-xs rounded-lg border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-surface-400 uppercase mb-0.5">
                        Rate ($/hr)
                      </label>
                      <input
                        type="number"
                        value={logRate}
                        onChange={(e) => setLogRate(e.target.value)}
                        className="w-full p-1.5 text-xs rounded-lg border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800"
                      />
                    </div>
                    <div className="flex flex-col justify-end">
                      <label className="flex items-center space-x-1.5 text-xs cursor-pointer pb-1.5">
                        <input
                          type="checkbox"
                          checked={logBillable}
                          onChange={(e) => setLogBillable(e.target.checked)}
                          className="rounded text-brand-600 w-3.5 h-3.5"
                        />
                        <span className="font-semibold text-surface-700 dark:text-surface-300">
                          Billable
                        </span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <input
                      type="text"
                      value={logDesc}
                      onChange={(e) => setLogDesc(e.target.value)}
                      placeholder="Work notes (e.g. Completed initial design pass)..."
                      className="w-full p-1.5 text-xs rounded-lg border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 text-surface-900 dark:text-surface-100"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-1.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-xs font-semibold"
                  >
                    Save Time Entry
                  </button>
                </form>
              )}

              {/* Logged Time Entries List */}
              {timeEntries.length > 0 ? (
                <div className="space-y-1.5 mb-2 max-h-36 overflow-y-auto">
                  {timeEntries.map((entry) => (
                    <div
                      key={entry.id}
                      className="p-2 rounded-xl bg-surface-50 dark:bg-surface-800/40 border border-surface-200/60 dark:border-surface-700/60 flex items-center justify-between text-xs"
                    >
                      <div>
                        <span className="font-bold text-surface-900 dark:text-surface-100 mr-2">
                          {entry.hours} hrs
                        </span>
                        <span className="text-surface-500 text-[11px]">
                          {entry.description}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-[10px]">
                        <span className="text-surface-400">{entry.userName}</span>
                        {entry.isBillable ? (
                          <span className="px-1.5 py-0.5 rounded font-mono font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                            ${(entry.hours * entry.hourlyRate).toFixed(0)}
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded font-bold bg-surface-200 text-surface-500">
                            Non-billable
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-2.5 text-center text-xs text-surface-400 bg-surface-50/40 dark:bg-surface-800/20 rounded-xl">
                  No time logs recorded yet. Track effort to monitor budget burndown.
                </div>
              )}
            </div>

            {/* Ticket Chat & Discussion Thread */}
            <div className="pt-4 border-t border-surface-200 dark:border-surface-800">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-surface-400 flex items-center">
                  <MessageSquare className="w-4 h-4 mr-1.5 text-brand-500" />
                  Ticket Chat & Discussion
                </span>
                <span className="text-[10px] text-surface-400 bg-surface-100 dark:bg-surface-800 px-2 py-0.5 rounded-full font-medium">
                  Use @name to notify teammates
                </span>
              </div>

              <div className="space-y-3 mb-4">
                {task.comments?.map((comment: any) => {
                  const isOwn =
                    user &&
                    (comment.userId === user.id ||
                      user.role === 'admin' ||
                      user.role === 'manager');
                  const isEditing = editingCommentId === comment.id;
                  return (
                    <div key={comment.id} className="flex items-start space-x-3 text-xs group">
                      <Avatar src={comment.userAvatar} name={comment.userName} size="sm" />
                      <div
                        className={`flex-1 p-3 rounded-2xl border ${
                          comment.isReviewFeedback || comment.is_review_feedback
                            ? 'bg-rose-50/50 dark:bg-rose-950/20 border-rose-300 dark:border-rose-800'
                            : 'bg-surface-50 dark:bg-surface-800/60 border-surface-200/60 dark:border-surface-700/60'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold text-surface-900 dark:text-surface-100">
                              {comment.userName}
                            </span>
                            <RoleBadge role={comment.userRole} />
                            {(comment.isReviewFeedback || comment.is_review_feedback) && (
                              <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/30 flex items-center gap-1">
                                <RotateCcw className="w-2.5 h-2.5" />
                                Revision Feedback
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-surface-400">
                              {new Date(comment.createdAt).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                            {isOwn && !isEditing && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingCommentId(comment.id);
                                    setEditingCommentText(comment.content);
                                  }}
                                  className="text-surface-400 hover:text-brand-600"
                                  title="Edit comment"
                                >
                                  <Edit3 className="w-3 h-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteComment(comment.id)}
                                  className="text-surface-400 hover:text-rose-500"
                                  title="Delete comment"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {isEditing ? (
                          <div className="space-y-2 mt-1">
                            <textarea
                              rows={2}
                              value={editingCommentText}
                              onChange={(e) => setEditingCommentText(e.target.value)}
                              className="w-full p-2 rounded-lg bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-700 text-xs"
                            />
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleSaveCommentEdit(comment.id)}
                                className="px-2.5 py-1 rounded-lg bg-brand-600 text-white font-semibold text-[11px]"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingCommentId(null)}
                                className="px-2.5 py-1 rounded-lg text-surface-500 hover:bg-surface-200 dark:hover:bg-surface-700 text-[11px]"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-surface-700 dark:text-surface-300 whitespace-pre-wrap leading-relaxed">
                            <LinkifiedText text={comment.content} mentionNames={[...scopedMembers, ...teamMembers].map((m) => m?.fullName)} />
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}

                {(!task.comments || task.comments.length === 0) && (
                  <div className="text-center py-4 text-xs text-surface-400">
                    No comments yet. Start the conversation with team members using @mentions.
                  </div>
                )}
              </div>

              {/* Quick Mention Pills */}
              {scopedMembers && scopedMembers.length > 0 && (
                <div className="flex items-center gap-1.5 overflow-x-auto pb-2 mb-2 scrollbar-none text-[11px]">
                  <span className="text-[10px] text-surface-400 font-semibold uppercase shrink-0">
                    Mention:
                  </span>
                  {scopedMembers.slice(0, 6).map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => {
                        const mentionTag = `@${m.fullName} `;
                        if (!newComment.includes(mentionTag)) {
                          setNewComment((prev) => `${prev} ${mentionTag}`.trimStart());
                        }
                      }}
                      className="px-2 py-0.5 rounded-lg bg-surface-100 hover:bg-brand-50 hover:text-brand-600 dark:bg-surface-800 dark:hover:bg-brand-900/40 text-surface-600 dark:text-surface-300 transition-colors shrink-0"
                    >
                      @{m.fullName.split(' ')[0]}
                    </button>
                  ))}
                </div>
              )}

              {/* Comment Input */}
              {can('task.comment') ? (
                <form onSubmit={handleAddComment} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a message in ticket chat... (e.g. @Alex please review)"
                    className="flex-1 px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                  <button
                    type="submit"
                    disabled={!newComment.trim()}
                    className="p-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 disabled:opacity-50 transition-colors shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              ) : (
                <p className="text-xs text-surface-400 italic">Viewers have read-only access and cannot post comments.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Interactive Document Preview Modal */}
      <DocumentPreviewModal
        isOpen={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        file={previewFile}
      />

      {/* Existing Project Members Modal */}
      {isViewingProjectMembers && task?.projectId && (
        <ExistingProjectMembersModal
          isOpen={isViewingProjectMembers}
          onClose={() => setIsViewingProjectMembers(false)}
          projectName={allProjects.find((p) => p.id === task.projectId)?.name || task.projectName || 'Selected Project'}
          members={scopedMembers}
        />
      )}

      {/* Request Changes Modal */}
      {task && (
        <RequestChangesModal
          isOpen={isRequestChangesOpen}
          taskTitle={task.title}
          taskCode={task.code}
          isSubmitting={isSubmittingReviewAction}
          onClose={() => setIsRequestChangesOpen(false)}
          onSubmit={handleRequestChangesSubmit}
        />
      )}
    </div>
  );
};
